import { gql, type TypedDocumentNode } from '@apollo/client';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove.ts';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags.tsx';
import { isType, isTypeFn } from '@sorare/core/src/lib/gql/index.ts';

import { ConnectTaskWidget } from '../ConnectTaskWidget/index.tsx';
import { DailyActionTaskWidget } from '../DailyActionTaskWidget/index.tsx';
import { DailyPickerTaskWidget } from '../DailyPickerTaskWidget/index.tsx';
import { FeaturedCollectionTaskWidget } from '../FeaturedCollectionTaskWidget/index.tsx';
import type {
  SeriesMissionsWidgets_cardSetRoot,
  SeriesMissionsWidgets_currentUser,
} from './__generated__/index.graphql.ts';

type Props = {
  currentUser: SeriesMissionsWidgets_currentUser;
  refetch: () => void;
  cardSet: SeriesMissionsWidgets_cardSetRoot;
  showConnectTaskDialog: boolean;
  onConnectTaskDialogClose: () => void;
  onConnectTaskDialogOpen: () => void;
};
export const SeriesMissionsWidgets = ({
  currentUser,
  refetch,
  cardSet,
  showConnectTaskDialog,
  onConnectTaskDialogClose,
  onConnectTaskDialogOpen,
}: Props) => {
  const {
    flags: { enableInviteFriends },
  } = useFeatureFlags();
  const isDesktop = useIsDesktopAndAbove();

  const hasClaimableCollectionTasks =
    (currentUser?.setCollectionTasks || []).filter(
      task => task.aasmState === ManagerProgressionTaskState.COMPLETED
    ).length > 0;

  const playDailyActionTask = isType(
    currentUser?.dailyRunTask,
    'DailyActionTask'
  )
    ? currentUser?.dailyRunTask
    : null;

  const featuredCollectionTask = currentUser?.setCollectionTasks
    ?.filter(
      task =>
        isType(task, 'CompleteCollectionsCountTask') ||
        isType(task, 'CardsCountTask')
    )
    .at(0);

  const dailyPickerTask = isType(
    currentUser?.setDecisiveActionTask,
    'DecisivePlayerPickerTask'
  )
    ? currentUser?.setDecisiveActionTask
    : null;

  const inviteTasks = (currentUser?.inviteTasksAsReferrer || []).filter(
    isTypeFn('InviteTaskAsReferrer')
  );

  return (
    <Vertical center gap={2}>
      {!!playDailyActionTask && !isDesktop && (
        <DailyActionTaskWidget dailyActionTask={playDailyActionTask} />
      )}
      {!!featuredCollectionTask && !isDesktop && (
        <Badge
          badgeContent={
            (currentUser?.setCollectionTasks || []).filter(
              task => task.aasmState === ManagerProgressionTaskState.COMPLETED
            ).length
          }
          overlap="circular"
        >
          <FeaturedCollectionTaskWidget
            featuredCollectionTask={featuredCollectionTask}
            hasClaimableCollectionTasks={hasClaimableCollectionTasks}
          />
        </Badge>
      )}
      {!!dailyPickerTask && (
        <DailyPickerTaskWidget task={dailyPickerTask} refetch={refetch} />
      )}
      {!!inviteTasks.length && !!enableInviteFriends && (
        <ConnectTaskWidget
          currentUser={currentUser}
          cardSet={cardSet}
          open={showConnectTaskDialog}
          onClose={onConnectTaskDialogClose}
          onOpen={onConnectTaskDialogOpen}
        />
      )}
    </Vertical>
  );
};

SeriesMissionsWidgets.fragments = {
  currentUser: gql`
    fragment SeriesMissionsWidgets_currentUser on CurrentUser {
      slug
      dailyRunTask(sport: $sport) {
        id
        name
        aasmState
        ... on DailyActionTask {
          id
          ...DailyActionTaskWidget_dailyActionTask
        }
      }
      setCollectionTasks(sport: $sport) {
        id
        aasmState
        ...FeaturedCollectionTaskWidget_taskInterface
      }
      setDecisiveActionTask(sport: $sport) {
        id
        ...DailyPickerTaskWidget_decisivePlayerPickerTask
      }
      ...ConnectTaskWidget_currentUser
    }
    ${DailyActionTaskWidget.fragments.dailyActionTask}
    ${FeaturedCollectionTaskWidget.fragments.taskInterface}
    ${DailyPickerTaskWidget.fragments.decisivePlayerPickerTask}
    ${ConnectTaskWidget.fragments.currentUser}
  ` as TypedDocumentNode<SeriesMissionsWidgets_currentUser>,
  cardSet: gql`
    fragment SeriesMissionsWidgets_cardSetRoot on CardSetRoot {
      id
      ...ConnectTaskWidget_cardSetRoot
    }
    ${ConnectTaskWidget.fragments.cardSetRoot}
  ` as TypedDocumentNode<SeriesMissionsWidgets_cardSetRoot>,
};
